"use client"
import * as React from "react"
import { cn } from "@/lib/utils"

type Variant = "default" | "outline" | "ghost" | "destructive" | "secondary" | "accent"
type Size = "sm" | "md" | "lg" | "icon"

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  asChild?: boolean
}

const variantStyles: Record<Variant, string> = {
  default: "bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary)]/90",
  secondary: "bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--muted)]",
  outline: "border border-[var(--border)] bg-transparent hover:border-[var(--foreground)] text-[var(--foreground)]",
  ghost: "bg-transparent hover:bg-[var(--secondary)] text-[var(--foreground)]",
  destructive: "bg-red-600 text-white hover:bg-red-700",
  accent: "bg-[var(--accent)] text-[var(--accent-foreground)] hover:bg-[var(--accent)]/90",
}

const sizeStyles: Record<Size, string> = {
  sm: "px-3.5 py-1.5 text-[13px]",
  md: "px-4 py-2 text-[13px]",
  lg: "px-6 py-2.5 text-sm",
  icon: "p-2",
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "md", ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius)] font-medium transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed tracking-wide",
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    />
  )
)
Button.displayName = "Button"
